import React, { useState, useEffect, useCallback } from "react";
import { Plus, X } from "lucide-react";

const PRESETS_KEY = "bc-color-presets";
const MAX_PRESETS = 8;

function loadPresets() {
  try {
    const raw = localStorage.getItem(PRESETS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_PRESETS) : [];
  } catch {
    return [];
  }
}

function savePresets(presets) {
  try {
    localStorage.setItem(PRESETS_KEY, JSON.stringify(presets.slice(0, MAX_PRESETS)));
  } catch {}
}

export default function ColorPicker({ label, value, onChange }) {
  const [presets, setPresets] = useState(loadPresets);
  const [hoveredPreset, setHoveredPreset] = useState(null);

  useEffect(() => {
    setPresets(loadPresets());
  }, []);

  const normalizeHex = (v) => {
    let s = (v || "").trim();
    if (!s) return "";
    if (!s.startsWith("#")) s = "#" + s;
    if (s.length === 7 && !/^#[0-9A-Fa-f]{6}$/.test(s)) return value;
    return s.slice(0, 7);
  };

  const addPreset = useCallback(() => {
    if (!value || presets.includes(value)) return;
    const updated = [value, ...presets].slice(0, MAX_PRESETS);
    setPresets(updated);
    savePresets(updated);
  }, [value, presets]);

  const removePreset = useCallback((color) => {
    const updated = presets.filter((c) => c !== color);
    setPresets(updated);
    savePresets(updated);
    setHoveredPreset(null);
  }, [presets]);

  return (
    <div>
      <label className="text-xs block mb-1">{label}</label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-12"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(normalizeHex(e.target.value))}
          className="flex-1 h-8 w-4 border rounded px-2 text-sm font-mono"
          placeholder="#3F256B"
        />
        <button
          type="button"
          onClick={addPreset}
          title="Save to presets"
          className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md border border-bookish-purple/15 hover:bg-bookish-purple/8 text-bookish-purple/50 hover:text-bookish-purple transition-colors"
        >
          <Plus size={13} />
        </button>
      </div>

      {presets.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {presets.map((color) => (
            <div
              key={color}
              className="relative"
              onMouseEnter={() => setHoveredPreset(color)}
              onMouseLeave={() => setHoveredPreset(null)}
            >
              <button
                type="button"
                onClick={() => onChange(color)}
                title={color}
                className="w-6 h-6 rounded-full border-2 transition-all"
                style={{
                  backgroundColor: color,
                  borderColor: value === color ? "rgba(63,37,107,0.6)" : "rgba(0,0,0,0.1)",
                  boxShadow: value === color ? "0 0 0 2px rgba(63,37,107,0.15)" : "none",
                }}
              />
              {hoveredPreset === color && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removePreset(color); }}
                  className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-red-500 text-white flex items-center justify-center shadow-sm"
                  title="Remove preset"
                >
                  <X size={8} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
